import moment from 'moment-timezone';
import { merge, Observable, of, timer } from 'rxjs';
import { map } from 'rxjs/operators';
import { DayOfWeek, parseTimeObjectFromString, TimeString, WEEK_IS_SECONDS } from './date';

export interface WeeklyPeriod<T = unknown> {
  startDay: DayOfWeek;
  startTime: TimeString;
  endDay: DayOfWeek;
  endTime: TimeString;
  data: T;
}

export type WeeklyPeriodSchedule<T = unknown> = Array<WeeklyPeriod<T>>;

export interface WeeklyPeriodSchedulerEvent<T = unknown> {
  event: 'start' | 'end';
  period: WeeklyPeriod<T>;
}

const mapEvent = <T = unknown>(
  event: WeeklyPeriodSchedulerEvent<T>['event'],
  period: WeeklyPeriodSchedulerEvent<T>['period'],
) => {
  return map((): WeeklyPeriodSchedulerEvent<T> => (
    {
      event,
      period,
    }
  ));
};

export const weeklyTimer = (startDue: number | Date) => {
  return timer(startDue, WEEK_IS_SECONDS);
};

export const weeklyPeriodScheduler = <T = unknown>(
  schedule: WeeklyPeriodSchedule<T>,
  nowEvent = false,
): Observable<WeeklyPeriodSchedulerEvent<T>> => {
  const now = moment();

  const timers = schedule.map((period) => {
    const start = now.clone().set({
      ...parseTimeObjectFromString(period.startTime),
      isoWeekday: period.startDay,
    });

    const end = now.clone().set({
      ...parseTimeObjectFromString(period.endTime),
      isoWeekday: period.endDay,
    });

    if (now.isBetween(start, end)) {
      return [
        nowEvent ? of('now').pipe(
          mapEvent('start', period),
        ) : undefined,
        weeklyTimer(start.add('1', 'weeks').toDate()).pipe(
          mapEvent('start', period),
        ),
        weeklyTimer(end.toDate()).pipe(
          mapEvent('end', period),
        ),
      ].filter(Boolean);
    }

    if (end.isBefore(now)) {
      start.add('1', 'weeks');
      end.add('1', 'weeks');
    }

    return [
      weeklyTimer(start.toDate()).pipe(
        mapEvent('start', period),
      ),
      weeklyTimer(end.toDate()).pipe(
        mapEvent('end', period),
      ),
    ];
  }).flat();

  return merge(...timers);
};
